import {
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  Suspense,
  useEffect,
  useState,
} from "react";
import { navigateTo } from "../../router";
import { resolveReplayShare } from "../services/game-api";

/**
 * Game-agnostic replay room. Resolves a share code to `{ gameType, events }`
 * and dispatches to the matching per-game replay view. The viewer is the sole
 * host and controls the stepper (single-player for now). Backend-free to
 * render once resolved; reads only the standalone replay-share resource.
 */
interface ReplayRoomScreenProps {
  readonly shareCode: string;
}

const REPLAY_ROOM_VIEWS: Record<
  string,
  LazyExoticComponent<ComponentType<{ events: readonly unknown[] }>>
> = {
  yinsh: lazy(() =>
    import("../../games/yinsh/YinshReplayRoomView").then((m) => ({
      default: m.YinshReplayRoomView,
    })),
  ),
};

function ReplayRoomMessage({
  testId,
  title,
  detail,
}: {
  testId: string;
  title: string;
  detail: string;
}) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary"
      data-testid={testId}
    >
      <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
        <h1 className="mb-2 font-semibold text-lg">{title}</h1>
        <p className="mb-4 text-sm text-text-secondary">{detail}</p>
        <button
          type="button"
          className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
          onClick={() => navigateTo("/replay/lobby")}
        >
          돌아가기
        </button>
      </section>
    </div>
  );
}

export function ReplayRoomScreen({ shareCode }: ReplayRoomScreenProps) {
  const [status, setStatus] = useState<"loading" | "error" | "ready">(
    "loading",
  );
  const [gameType, setGameType] = useState<string>("");
  const [events, setEvents] = useState<readonly unknown[]>([]);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!shareCode) {
      setStatus("error");
      setError("공유 코드가 없습니다");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    resolveReplayShare(shareCode)
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.data?.events) {
          setStatus("error");
          setError(res.error ?? "리플레이를 찾을 수 없습니다");
          return;
        }
        setGameType(res.data.gameType);
        setEvents(res.data.events);
        setStatus("ready");
      })
      .catch((cause) => {
        if (cancelled) return;
        setStatus("error");
        setError(
          cause instanceof Error
            ? cause.message
            : "리플레이를 불러올 수 없습니다",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [shareCode]);

  if (status === "loading") {
    return (
      <ReplayRoomMessage
        testId="replay-room-loading"
        title="리플레이를 불러오는 중…"
        detail={shareCode}
      />
    );
  }

  if (status === "error") {
    return (
      <ReplayRoomMessage
        testId="replay-room-error"
        title="리플레이를 찾을 수 없습니다"
        detail={error}
      />
    );
  }

  const View = REPLAY_ROOM_VIEWS[gameType];
  if (!View) {
    return (
      <ReplayRoomMessage
        testId="replay-room-unsupported"
        title="지원하지 않는 게임입니다"
        detail={gameType}
      />
    );
  }

  return (
    <div data-testid="replay-room-screen">
      <Suspense
        fallback={
          <ReplayRoomMessage
            testId="replay-room-loading"
            title="리플레이를 불러오는 중…"
            detail={gameType}
          />
        }
      >
        <View events={events} />
      </Suspense>
    </div>
  );
}
